import React, { useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
} from "@mui/material";
import Form from "@rjsf/mui";
import validator from "@rjsf/validator-ajv8";
import CodeEditor from "../common/code-editor";

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

// Utility function to clean data by removing regex and vars from items (but keep duplicate)
const cleanFormData = data => {
  if (!data || !data.items) return data;

  return {
    ...data,
    items: data.items.map(item => {
      const { regex, vars, ...cleanItem } = item;
      return cleanItem; // duplicate field is preserved
    }),
  };
};

// Utility function to clean JSON content
const cleanJsonContent = jsonString => {
  try {
    const data = JSON.parse(jsonString);
    const cleanedData = cleanFormData(data);
    return JSON.stringify(cleanedData, null, 2);
  } catch (error) {
    return jsonString; // Return original if parsing fails
  }
};

// Utility function to clean schema by removing regex and vars from items (but keep duplicate)
const cleanSchema = schema => {
  if (!schema || !schema.properties || !schema.properties.items) return schema;

  const cleanedSchema = JSON.parse(JSON.stringify(schema)); // Deep clone

  if (
    cleanedSchema.properties.items.items &&
    cleanedSchema.properties.items.items.properties
  ) {
    const { regex, vars, ...cleanProperties } =
      cleanedSchema.properties.items.items.properties;
    cleanedSchema.properties.items.items.properties = cleanProperties;

    // Add duplicate field to schema if it doesn't exist
    if (!cleanedSchema.properties.items.items.properties.duplicate) {
      cleanedSchema.properties.items.items.properties.duplicate = {
        type: "boolean",
        title: "Duplicate Status",
        description: "Indicates if this item is a duplicate (readonly)",
      };
    }
  }

  return cleanedSchema;
};

// Utility function to clean uiSchema by removing regex and vars configurations (but add duplicate config)
const cleanUiSchema = uiSchema => {
  if (!uiSchema || !uiSchema.items || !uiSchema.items.items) return uiSchema;

  const cleanedUiSchema = JSON.parse(JSON.stringify(uiSchema)); // Deep clone

  if (cleanedUiSchema.items.items) {
    const { regex, vars, ...cleanItems } = cleanedUiSchema.items.items;
    cleanedUiSchema.items.items = {
      ...cleanItems,
      // Make duplicate field readonly and add warning styling
      duplicate: {
        "ui:readonly": true,
        "ui:widget": "DuplicateWarningWidget",
        "ui:options": {
          label: false, // Hide the default label since we'll show custom warning
        },
      },
    };
  }

  return cleanedUiSchema;
};

// Custom widget for duplicate field that shows warning when true
const DuplicateWarningWidget = props => {
  const { value } = props;

  if (!value) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Alert severity="warning" sx={{ display: "flex", alignItems: "center" }}>
        <Typography variant="body2">
          <strong>Duplicate Detected:</strong> This quick word already exists in
          the system
        </Typography>
        <Chip label="DUPLICATE" color="warning" size="small" sx={{ ml: 2 }} />
      </Alert>
    </Box>
  );
};

// Custom widget for script field using CodeEditor
const CodeEditorWidget = props => {
  const { value, onChange, disabled, readonly } = props;

  const handleCodeChange = useCallback(
    instance => {
      const code = instance.getValue();
      onChange(code);
    },
    [onChange],
  );

  return (
    <Box sx={{ mt: 1 }}>
      <CodeEditor
        value={value || ""}
        editable={!disabled && !readonly}
        onChange={handleCodeChange}
        height="300px"
      />
    </Box>
  );
};

// Custom widgets object
const customWidgets = {
  CodeEditorWidget: CodeEditorWidget,
  DuplicateWarningWidget: DuplicateWarningWidget,
};

function QuickWordConfigDialog({
  open,
  onClose,
  selectedCommand,
  formData,
  onFormChange,
  onSave,
  isProcessing,
  schema,
  createUiSchema,
  editUiSchema,
  jsonContent,
  onJsonChange,
  jsonError,
}) {
  const [tabValue, setTabValue] = useState(0);

  // Clean the form data to remove regex and vars from items
  const cleanedFormData = useMemo(() => {
    return cleanFormData(formData);
  }, [formData]);

  // Clean the JSON content to remove regex and vars from items
  const cleanedJsonContent = useMemo(() => {
    return cleanJsonContent(jsonContent);
  }, [jsonContent]);

  // Clean the schema to remove regex and vars field definitions
  const cleanedSchema = useMemo(() => {
    return cleanSchema(schema);
  }, [schema]);

  // Clean the uiSchemas to remove regex and vars field configurations
  const cleanedCreateUiSchema = useMemo(() => {
    return cleanUiSchema(createUiSchema);
  }, [createUiSchema]);

  const cleanedEditUiSchema = useMemo(() => {
    return cleanUiSchema(editUiSchema);
  }, [editUiSchema]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        {selectedCommand && selectedCommand.id
          ? `Edit Command: ${selectedCommand.id}`
          : "Create New Command"}
      </DialogTitle>
      <DialogContent dividers>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <Tab label="Form Editor" />
          <Tab label="JSON Editor" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {cleanedFormData && (
            <Form
              schema={cleanedSchema}
              formData={cleanedFormData}
              onChange={onFormChange}
              validator={validator}
              uiSchema={
                selectedCommand && selectedCommand.id
                  ? cleanedEditUiSchema
                  : cleanedCreateUiSchema
              }
              widgets={customWidgets}
            />
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Edit the command configuration as JSON. Changes will be reflected
              in the form editor.
            </Typography>
            {jsonError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {jsonError}
              </Alert>
            )}
            <CodeEditor
              value={cleanedJsonContent}
              editable={true}
              onChange={onJsonChange}
              height="60vh"
            />
          </Box>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          disabled={isProcessing === "save"}
        >
          Save Command
          {isProcessing === "save" && (
            <CircularProgress size={20} sx={{ ml: 1 }} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default QuickWordConfigDialog;
